/**
 * @module config
 * @description Central configuration for Preparo application
 *
 * All magic numbers and configurable values should be defined here.
 * Environment variables (VITE_*) can override default values.
 *
 * @example
 * import { config } from '@/config';
 * const maxSize = config.files.maxSize;
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ProxyConfig {
  /** Whether proxy mode is enabled (uses Spectral Cloud Portal for auth) */
  enabled: boolean;
  /** Supabase URL */
  supabaseUrl: string;
  /** Supabase anonymous key */
  supabaseAnonKey: string;
  /** Pandoc proxy function name */
  pandocProxyFunction: string;
  /** Portal URL for redirects (login, settings) */
  portalUrl: string;
}

interface ApiConfig {
  /** Base URL for the main API */
  baseUrl: string;
  /** Pandoc conversion server URL (direct mode) */
  pandocUrl: string;
  /** Request timeout in milliseconds */
  timeout: number;
  /** Proxy mode configuration for Spectral Cloud integration */
  proxy: ProxyConfig;
}

interface FilesConfig {
  /** Maximum file size for upload (bytes) */
  maxSize: number;
  /** File size warning threshold (bytes) */
  warningSizeThreshold: number;
  /** Maximum files per batch conversion */
  maxBatchFiles: number;
  /** Maximum filename length */
  maxFilenameLength: number;
  /** Allowed file extensions for import */
  allowedExtensions: readonly string[];
  /** MIME types for allowed files */
  allowedMimeTypes: readonly string[];
}

interface EditorConfig {
  /** Maximum content length (characters) */
  maxContentLength: number;
  /** Auto-save delay in milliseconds */
  autoSaveDelay: number;
  /** Maximum undo/redo history entries */
  maxHistorySize: number;
  /** Maximum consecutive blank lines after sanitization */
  maxConsecutiveBlankLines: number;
  /** Default summary length for excerpts */
  defaultSummaryLength: number;
  /** Default reading speed (words per minute) */
  defaultReadingSpeed: number;
  /** Threshold for detecting significant content change (percentage) */
  significantChangeThreshold: number;
  /** Placeholder text for empty editor */
  placeholder: string;
}

interface UiConfig {
  /** Default projects panel width in pixels */
  projectsPanelWidth: number;
  /** Default documents panel width in pixels */
  docsPanelWidth: number;
  /** Minimum projects panel width */
  projectsPanelMinWidth: number;
  /** Maximum projects panel width */
  projectsPanelMaxWidth: number;
  /** Minimum documents panel width */
  docsPanelMinWidth: number;
  /** Maximum documents panel width */
  docsPanelMaxWidth: number;
  /** Minimum notes panel width */
  notesPanelMinWidth: number;
}

interface TimeoutsConfig {
  /** Time before "Saved" message disappears (ms) */
  savedStatusDuration: number;
  /** Threshold for "Saved recently" message (ms) */
  savedRecentlyThreshold: number;
  /** Delay before clearing saved message (ms) */
  clearSavedMessageDelay: number;
  /** Editor auto-focus delay (ms) */
  editorFocusDelay: number;
  /** Debounce delay for title update from H1 (ms) */
  titleUpdateDebounce: number;
}

interface ProgressConfig {
  /** Progress value: uploading started */
  uploading: number;
  /** Progress value: conversion in progress */
  converting: number;
  /** Progress value: completed */
  completed: number;
}

interface SyncConfig {
  /** Whether Git sync is enabled */
  enabled: boolean;
  /** Debounce delay for content changes (Markdown text) in ms */
  debounceContentMs: number;
  /** Debounce delay for structural changes (new doc, delete, reorder) in ms */
  debounceStructureMs: number;
  /** Maximum delay between syncs during continuous writing in ms */
  debounceForceMs: number;
  /** File size threshold above which binaries go through LFS staging (bytes) */
  lfsStagingThresholdBytes: number;
  /** Edge Function name for git-sync */
  edgeFunctionName: string;
}

interface DevConfig {
  /** Development server port */
  port: number;
  /** Enable verbose logging */
  verboseLogging: boolean;
}

export interface AppConfig {
  api: ApiConfig;
  files: FilesConfig;
  editor: EditorConfig;
  ui: UiConfig;
  timeouts: TimeoutsConfig;
  progress: ProgressConfig;
  sync: SyncConfig;
  dev: DevConfig;
}

// ============================================================================
// ENVIRONMENT VARIABLE HELPERS
// ============================================================================

/**
 * Get environment variable with fallback
 * Works in both browser (Vite) and Node.js environments
 */
function getEnv(key: string, defaultValue: string): string {
  // Vite environment (browser)
  if (typeof import.meta !== "undefined" && import.meta.env) {
    return (import.meta.env[key] as string | undefined) ?? defaultValue;
  }
  // Node.js environment
  if (typeof process !== "undefined" && process.env) {
    return process.env[key] ?? defaultValue;
  }
  return defaultValue;
}

/**
 * Parse environment variable as number
 */
function getEnvNumber(key: string, defaultValue: number): number {
  const value = getEnv(key, "");
  if (value === "") return defaultValue;
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Parse environment variable as boolean
 */
function getEnvBoolean(key: string, defaultValue: boolean): boolean {
  const value = getEnv(key, "");
  if (value === "") return defaultValue;
  return value === "true";
}

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Application configuration
 */
export const config: AppConfig = {
  // --------------------------------------------------------------------------
  // API Configuration
  // --------------------------------------------------------------------------
  api: {
    baseUrl: getEnv("VITE_API_URL", "http://localhost:8000"),
    pandocUrl: getEnv("VITE_PANDOC_API_URL", "http://localhost:3030"),
    timeout: getEnvNumber("VITE_API_TIMEOUT", 30000),
    proxy: {
      /** Proxy mode enabled when Supabase URL is configured */
      enabled: getEnvBoolean("VITE_USE_PROXY_MODE", false),
      /** Supabase URL */
      supabaseUrl: getEnv("VITE_SUPABASE_URL", ""),
      /** Supabase anonymous key */
      supabaseAnonKey: getEnv("VITE_SUPABASE_ANON_KEY", ""),
      /** Pandoc proxy function name */
      pandocProxyFunction: "pandoc-proxy",
      /** Portal URL for redirects (login, settings) */
      portalUrl: getEnv("VITE_PORTAL_URL", "http://localhost:5173"),
    },
  },

  // --------------------------------------------------------------------------
  // File Handling
  // --------------------------------------------------------------------------
  files: {
    maxSize: getEnvNumber("VITE_MAX_FILE_SIZE", 20 * 1024 * 1024),
    warningSizeThreshold: 10 * 1024 * 1024,
    maxBatchFiles: 20,
    maxFilenameLength: 200,
    allowedExtensions: [
      ".docx",
      ".doc",
      ".odt",
      ".epub",
      ".html",
      ".htm",
      ".xml",
      ".tei",
      ".md",
      ".markdown",
    ] as const,
    allowedMimeTypes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
      "application/vnd.oasis.opendocument.text",
      "application/epub+zip",
      "text/html",
      "application/xhtml+xml",
      "application/xml",
      "text/xml",
      "text/markdown",
      "text/x-markdown",
    ] as const,
  },

  // --------------------------------------------------------------------------
  // Editor Configuration
  // --------------------------------------------------------------------------
  editor: {
    maxContentLength: getEnvNumber("VITE_MAX_CONTENT_LENGTH", 1000000),
    autoSaveDelay: getEnvNumber("VITE_AUTOSAVE_DELAY", 1000),
    maxHistorySize: 50,
    maxConsecutiveBlankLines: 3,
    defaultSummaryLength: 150,
    defaultReadingSpeed: 200,
    significantChangeThreshold: 0.05,
    placeholder: "Start writing your notes...",
  },

  // --------------------------------------------------------------------------
  // UI Configuration
  // --------------------------------------------------------------------------
  ui: {
    projectsPanelWidth: 250,
    docsPanelWidth: 400,
    projectsPanelMinWidth: 200,
    projectsPanelMaxWidth: 400,
    docsPanelMinWidth: 300,
    docsPanelMaxWidth: 600,
    notesPanelMinWidth: 400,
  },

  // --------------------------------------------------------------------------
  // Timeouts and Delays
  // --------------------------------------------------------------------------
  timeouts: {
    savedStatusDuration: 5000,
    savedRecentlyThreshold: 60000,
    clearSavedMessageDelay: 3000,
    editorFocusDelay: 100,
    titleUpdateDebounce: 1500,
  },

  // --------------------------------------------------------------------------
  // Progress Indicators
  // --------------------------------------------------------------------------
  progress: {
    uploading: 0,
    converting: 50,
    completed: 100,
  },

  // --------------------------------------------------------------------------
  // Git Sync — Persistence via Gitea
  // --------------------------------------------------------------------------
  sync: {
    enabled: getEnvBoolean("VITE_SYNC_ENABLED", false),
    debounceContentMs: getEnvNumber("VITE_SYNC_DEBOUNCE_CONTENT_MS", 60_000),
    debounceStructureMs: getEnvNumber("VITE_SYNC_DEBOUNCE_STRUCTURE_MS", 5_000),
    debounceForceMs: getEnvNumber("VITE_SYNC_DEBOUNCE_FORCE_MS", 300_000),
    lfsStagingThresholdBytes: getEnvNumber("VITE_LFS_STAGING_THRESHOLD_BYTES", 4_194_304),
    edgeFunctionName: "git-sync",
  },

  // --------------------------------------------------------------------------
  // Development
  // --------------------------------------------------------------------------
  dev: {
    port: getEnvNumber("VITE_DEV_PORT", 3000),
    verboseLogging: getEnvBoolean("VITE_VERBOSE_LOGGING", false),
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

// Named exports for specific sections
export const { api, files, editor, ui, timeouts, progress, sync, dev } = config;

// Default export
export default config;
